import { FormControl, FormControlLabel, makeStyles } from '@material-ui/core'
import useOptimisticValue from 'elements/input/useOptimisticValue'
import { Radio, RadioGroup } from 'opensolar-ui'
import { ChangeEvent } from 'react'
import { useTranslate } from 'react-admin'
import { Theme } from 'types/themes'
import type { BasicRendererComponentPropsTypeV2 } from '../typeV2'
import useLimitedOptionsController from './useLimitedOptionsController'

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '22px',
  },
  filterOptions: {
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'left',
    margin: '0',
    padding: '0px',
    '& .MuiFormControlLabel-label': {
      fontWeight: 400,
      lineHeight: '20px',
    },
    '& .MuiIconButton-root, .OSUI-Radio-root': {
      padding: '3px 12px',
    },
  },
}))

/*
  A radio renderer for hardware filters
*/
const BasicRadioButtonRendererV2 = <OptionValue extends string | number>({
  id,
  label,
  options,
  disabled,
  onSelect,
  defaultValue,
  additionalProps,
  value,
}: BasicRendererComponentPropsTypeV2<OptionValue, OptionValue>) => {
  const classes = useStyles({ disabled: !!disabled })
  const { optimisticValue, updateOptimisticValue } = useOptimisticValue<OptionValue>({ defaultValue, value })
  const { limitedOptions, LimitedOptionsController } = useLimitedOptionsController(options)
  const translate = useTranslate()

  const handleChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
    const newValue = event.target.value as OptionValue
    const finalValue = additionalProps?.rendererOnChangeMutator
      ? additionalProps.rendererOnChangeMutator({
          value: newValue,
          oldValue: optimisticValue,
          options: options,
          selectedOption: limitedOptions.find((option) => option.value === newValue)!,
        })
      : newValue
    // Ensure the radio UI responds immediately on click, even if the parent filter state update is debounced
    // or the page is busy re-rendering / refetching.
    updateOptimisticValue(finalValue)
    onSelect(finalValue, limitedOptions.find((option) => option.value?.toString() === finalValue?.toString())!)
  }

  return (
    <FormControl id={id} disabled={disabled}>
      <RadioGroup value={optimisticValue} onChange={handleChange}>
        {limitedOptions.map((option, i) => {
          return (
            <FormControlLabel
              className={classes.filterOptions}
              value={option.value}
              key={option.id}
              control={
                <Radio size="small" color="info" checked={optimisticValue?.toString() === option.value?.toString()} />
              }
              label={
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                  {translate(option.title)}
                  {option.suffix}
                </span>
              }
            />
          )
        })}
      </RadioGroup>

      {!disabled && LimitedOptionsController}
    </FormControl>
  )
}

export default BasicRadioButtonRendererV2
